import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  handleLoginApi,
  handleOtpLoginApi,
  handleSendOtpApi,
} from "../../api/login.api";
import {
  getToken,
  getRole,
  setToken,
  clearToken,
  getUserId,
  getUser,
  getMobileNumber,
} from "../../utils/auth";
import { IUser } from "../../models/userData.model";
import { handleLogoutApi } from "../../api/logout.api";

interface AuthState {
  currentUser: IUser | null;
  token: string | null;
  role: string | null;
  loading: boolean;
  error: string | null;
  message: string | null;
  userId: string;
  userName: IUser | null;
  mobileNumber: string;
  manager: string | null;
  warehouseAssigned: string | null;
}

const initialState: AuthState = {
  currentUser: getUser() || null,
  token: getToken() || null,
  role: getRole() || null,
  loading: false,
  error: null,
  message: null,
  userId: getUserId() || "",
  userName: getUser(),
  mobileNumber: getMobileNumber() || "",
  manager: getUser()?.manager ?? null,
  warehouseAssigned: getUser()?.warehouseAssigned ?? null,
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    {
      email,
      username,
      password,
    }: { email: string; username: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const { token, role, user } = await handleLoginApi(
        email,
        username,
        password,
      );

      return { token, role, user };
    } catch (error: any) {
      return rejectWithValue("Login failed");
    }
  },
);

export const loginUserWithOtp = createAsyncThunk(
  "auth/loginUserWithOtp",
  async (
    { mobileNumber, otp }: { mobileNumber: string; otp: string },
    { rejectWithValue },
  ) => {
    try {
      const { token, role, user } = await handleOtpLoginApi(mobileNumber, otp);

      return { token, role, user };
    } catch (error: any) {
      return rejectWithValue("Login failed");
    }
  },
);
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async ({ mobileNumber }: { mobileNumber: string }, { rejectWithValue }) => {
    try {
      const { message } = await handleSendOtpApi(mobileNumber);
      return { message };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Otp Sending failed",
      );
    }
  },
);

export const logOutUser = createAsyncThunk("auth/logout", async () => {
  const res = await handleLogoutApi();
  return res;
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{ token: string; role: string; user: IUser }>,
        ) => {
          const { token, role, user } = action.payload;
          setToken(token, role, user);
          state.currentUser = user;
          state.loading = false;
          state.token = token;
          state.role = role;
          state.userId = user._id || "";
          state.userName = user;
          state.mobileNumber = user.mobileNumber;
          state.manager = user.manager ?? null;
          state.warehouseAssigned = user.warehouseAssigned ?? null;
        },
      )
      .addCase(loginUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(
        sendOtp.fulfilled,
        (state, action: PayloadAction<{ message: string }>) => {
          state.loading = false;
          state.message = action.payload.message;
        },
      )
      .addCase(loginUserWithOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        loginUserWithOtp.fulfilled,
        (
          state,
          action: PayloadAction<{ token: string; role: string; user: IUser }>,
        ) => {
          const { token, role, user } = action.payload;
          setToken(token, role, user);
          state.currentUser = user;
          state.loading = false;
          state.token = token;
          state.role = role;
          state.userId = user._id || "";
          state.userName = user;
          state.mobileNumber = user.mobileNumber;
          state.manager = user.manager ?? null;
          state.warehouseAssigned = user.warehouseAssigned ?? null;
        },
      )
      .addCase(loginUserWithOtp.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logOutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logOutUser.fulfilled, (state) => {
        clearToken();
        state.currentUser = null;
        state.token = null;
        state.role = null;
        state.userId = "";
        state.userName = null;
        state.mobileNumber = "";
        state.loading = false;
        state.error = null;
        state.warehouseAssigned = null;
      })

      .addCase(logOutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "An error occurred during logout";
      });
  },
});

export default authSlice.reducer;
